import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { get } from 'lodash-es';
/* Models */
import { InventoryEntities } from 'client/data/models/inventory';
/* Constants */
import { TrackingConstant } from 'client/tracking/constant';
import {
  EV_CREATIVE_IDS,
  TOOLTIPS_IDS,
  TOOLTIPS_TEXT,
  TOOLTIPS_VALUES,
} from 'site-modules/shared/components/inventory/ev-insights/ev-insights-constants';

/* Utils */
import { EventToolbox } from 'client/utils/event-toolbox';
import { isNew } from 'site-modules/shared/utils/inventory-utils/is-new';
import { isRangeMapEligible } from 'site-modules/shared/utils/inventory/is-range-map-eligible';
import { getStaticImageUrl } from 'client/utils/image-helpers';
import {
  getBatteryRangeMap,
  handleTooltipOpen,
} from 'site-modules/shared/components/inventory/ev-insights/utils/ev-insights';
/* Components */
import { TooltipItem } from 'site-modules/shared/components/tooltip-item/tooltip-item';
import { Link } from 'site-modules/shared/components/link/link';
import { EvRangeMap } from 'site-modules/shared/components/inventory/ev-insights/components/battery-range-and-charging/ev-range-map';
import { Accordion } from 'site-modules/shared/components/inventory/accordion/accordion';
import { EvAccordionItem } from 'site-modules/shared/components/inventory/ev-insights/components/ev-accordion-item/ev-accordion-item';

import './battery-range-and-charging.scss';

const EXTENDED_RANGE_BATTERY = 'Extended Range Battery';
const RECURRENT_LOGO_PATH = '/img/logos/recurrent-logo.png';

function BatteryRangeAndChargingUI({ vinData, isMobile = false, isSimplified = false }) {
  const rangeScore = get(vinData, 'thirdPartyInfo.recurrentInsights.rangeScore');
  const rangeScoreDescriptor = get(vinData, 'thirdPartyInfo.recurrentInsights.rangeScoreDescriptor');
  const expectedRangeMin = get(vinData, 'thirdPartyInfo.recurrentInsights.expectedRange.min');
  const expectedRangeMax = get(vinData, 'thirdPartyInfo.recurrentInsights.expectedRange.max');
  const reportUrl = get(vinData, 'thirdPartyInfo.recurrentInsights.reportUrl');
  const epaRange = get(vinData, 'vehicleInfo.partsInfo.electricityRange');
  const batteryCapacityKWH = get(vinData, 'vehicleInfo.styleInfo.fuel.batteryCapacityKwh');
  const isNewVehicle = isNew(get(vinData, 'type'));
  const isMapEligible = isRangeMapEligible(vinData);
  const hasExtendedRangeBattery = !!get(vinData, 'vehicleInfo.partsInfo.options', []).find(
    ({ name }) => name === EXTENDED_RANGE_BATTERY
  );

  const handleRecurrentLinkClick = useCallback(() => {
    EventToolbox.fireTrackAction({
      event_type: TrackingConstant.EVENT_TYPE_ACTION_COMPLETED,
      event_data: {
        action_name: TrackingConstant.ACTION_WEBSITE_CLICKOUT,
        action_cause: TrackingConstant.ACTION_CAUSE_LINK_CLICK,
        action_category: TrackingConstant.USER_ACTION_CATEGORY,
        subaction_name: 'recurrent_range',
        creative_id: EV_CREATIVE_IDS.RANGE_AND_BATTERY,
        value: reportUrl,
      },
    });
  }, [reportUrl]);

  if (isSimplified && !epaRange && !batteryCapacityKWH) {
    return null;
  }

  const content = (
    <div
      className={classnames('battery-range-and-charging-content', {
        'mt-1_5': !isMapEligible,
      })}
    >
      {!isSimplified && (
        <>
          <div
            className={classnames('d-flex flex-column', { 'flex-sm-row justify-content-sm-center': !isMapEligible })}
          >
            <div
              className={classnames('estimated-range text-center', {
                'pe-sm-2': !isMapEligible,
                'with-map': isMapEligible,
              })}
            >
              <span className="fw-bold size-24">
                {expectedRangeMin === expectedRangeMax
                  ? `${expectedRangeMin} mi`
                  : `${expectedRangeMin}-${expectedRangeMax} mi`}
              </span>
              <div className="size-16 mt-0_25">
                {TOOLTIPS_VALUES.ESTIMATED_VALUE}
                <TooltipItem
                  id={TOOLTIPS_IDS.ESTIMATED_ID}
                  className="top-0"
                  item={{
                    text: TOOLTIPS_TEXT.ESTIMATED_TEXT,
                    placement: 'bottom',
                  }}
                  onTooltipOpen={handleTooltipOpen}
                />
              </div>
            </div>
            {(isMobile || isMapEligible) && <hr className={classnames({ 'with-map': isMapEligible })} />}
            <div className={classnames('text-center', { 'ps-sm-2': !isMapEligible })}>
              <div>
                {rangeScoreDescriptor === 'Poor' && <i className="icon-warning2 size-24" aria-hidden />}{' '}
                <span className="fw-bold size-24">
                  {rangeScoreDescriptor} ({rangeScore})
                </span>
              </div>
              <div className="size-16 mt-0_25">
                {TOOLTIPS_VALUES.BATTERY_VALUE}
                <TooltipItem
                  id={TOOLTIPS_IDS.BATTERY_ID}
                  className="top-0"
                  item={{
                    text: TOOLTIPS_TEXT.BATTERY_TEXT,
                    placement: 'bottom',
                  }}
                  onTooltipOpen={handleTooltipOpen}
                />
              </div>
            </div>
          </div>
          <div
            className={classnames('d-flex mt-1_5 justify-content-between', {
              'justify-content-sm-center': !isMapEligible,
            })}
          >
            <div
              className={classnames('me-0_5 small text-gray-dark d-flex flex-column', {
                'flex-sm-row align-items-sm-center': !isMapEligible,
              })}
            >
              Provided by
              <img
                className={classnames({ 'ms-sm-0_5': !isMapEligible })}
                height={20}
                src={getStaticImageUrl(RECURRENT_LOGO_PATH)}
                alt="Recurrent logo"
                loading="lazy"
              />
            </div>
            <div className={classnames('text-primary-darker align-self-end', { 'align-self-sm-auto': !isMapEligible })}>
              <Link onClick={handleRecurrentLinkClick} target="_blank" to={reportUrl} rel="nofollow">
                View Free Report
                <span className="ms-0_5 icon-open_in_new" aria-hidden />
              </Link>
            </div>
          </div>
          <hr className="w-100" />
        </>
      )}

      <Accordion single={false}>
        <ul className="list-unstyled mb-0">
          {getBatteryRangeMap({ isNewVehicle, epaRange, batteryCapacityKWH }).map(({ id, ...restParams }) => (
            <EvAccordionItem key={id} id={id} {...restParams} />
          ))}
        </ul>
        {hasExtendedRangeBattery && (
          <div>
            <i className="icon-battery-full pe-0_5" aria-hidden />
            <em className="text-cool-gray-40 size-14">Includes extended range battery upgrade</em>
          </div>
        )}
      </Accordion>
    </div>
  );

  return (
    <section className="battery-range-and-charging" data-tracking-parent={EV_CREATIVE_IDS.RANGE_AND_BATTERY}>
      {isMapEligible ? <EvRangeMap vinData={vinData}>{content}</EvRangeMap> : content}
    </section>
  );
}

BatteryRangeAndChargingUI.propTypes = {
  vinData: InventoryEntities.InventoryVin.isRequired,
  isMobile: PropTypes.bool,
  isSimplified: PropTypes.bool,
};

export const BatteryRangeAndCharging = React.memo(BatteryRangeAndChargingUI);
